import React, { useMemo } from 'react';
import { SVG } from '../../../../../constants/assetLists';
import AppMenu from '../../../../../components/common/AppMenu';
import { ChatType } from './ChatBox';
import ChatInput from './ChatInput';
import AppButton from '../../../../../components/common/AppButton';
import { useAnnouncementTaskMutations } from '../../../../../hooks/useAnnouncementTaskMutations';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import QUERY_KEYS from '../../../../../constants/queryKeys';
import { handleGetAllUsersService } from '../../../../../services/users';
import { UsersListResponse } from '../../../../users/components/UserScreens/users';
import { ApiResponse } from '../../../../../services/axiosInstance';
import { useOperationsContext } from '../../../../../context/OperationsContextProvider';
import { ICONS } from '../../../../users/data';
import EditableChat from './EditableChat';
import FileBox from './FileBox';
import ChecklistBox from './ChecklistBox';
import {
  getCircleColor,
  getDepartmentFilterParams,
  getRoleTypeIcon,
  isSuperAdmin,
} from '../../../../../utils';
import { useClientsContext } from '../../../../../context/clients/ClientsContextProvider';
import AppConfirmationDialog from '../../../../../components/common/AppConfirmationDialog';

const MENUITEMS = [
  {
    title: 'עריכה',
    value: 'edit',
    icon: SVG.editGrayIcon,
    permission: 'update_announcement_task',
  },
  {
    title: 'מחיקה',
    value: 'delete',
    icon: SVG.deleteGrayIcon,
    permission: 'delete_announcement_task',
  },
];

function TaskCard({ task, announcement, index }: any) {
  const { selectedClient } = useClientsContext();
  const {
    setOpenAddEditAnnouncementDialog,
    setSelectedAnnouncementForEdit,
    setAnnouncementModalMode,
  } = useOperationsContext();

  const [showConfirmationDialog, setShowConfirmationDialog] =
    React.useState(false);
  const [anchorTaskEl, setAnchorTaskEl] = React.useState<HTMLElement | null>(
    null,
  );
  const [assigneeDropdownEl, setAssigneeDropdownEl] =
    React.useState<HTMLElement | null>(null);

  const [addTextEnabled, setAddTextEnabled] = React.useState(false);
  const [addTextIndex, setAddTextIndex] = React.useState<number | null>(null);
  const [addedText, setAddedText] = React.useState('');

  const {
    updateAnnouncementTaskCommentMutation,
    updateAnnouncementTaskMutation,
    createAnnouncementTaskCommentMutation,
    deleteAnnouncementTaskCommentMutation,
    deleteAnnouncementTaskMutation,
  } = useAnnouncementTaskMutations();

  const { data: allUsers } = useQuery<
    ApiResponse<UsersListResponse>,
    Error,
    UsersListResponse
  >({
    queryKey: [QUERY_KEYS.GET_ALL_USERS, selectedClient?._id],
    queryFn: () =>
      handleGetAllUsersService({
        status: 'active',
        clientId: selectedClient?._id,
        ...getDepartmentFilterParams(),
      }),
    enabled: !!selectedClient?._id,
  });

  const getIconColor = (role: Record<string, any>) => {
    const roleTypeVal = role?.roleType;
    let icon: string;
    if (Array.isArray(roleTypeVal)) {
      icon = getRoleTypeIcon(roleTypeVal?.[0]) || roleTypeVal?.[0]?.icon || '';
    } else {
      icon = getRoleTypeIcon(roleTypeVal) || role?.icon || '';
    }
    const color = role?.color || '';
    return { icon, color };
  };

  const ASSIGNEE_ITEMS = useMemo(() => {
    if (allUsers && allUsers?.data?.length > 0) {
      return allUsers.data
        .map((user: any) => {
          const roleIcons: Record<string, string> = getIconColor(
            user?.roles?.[0] || {},
          );
          return {
            title: `${user.firstName} ${user.lastName}`,
            value: user._id,
            icon:
              ICONS.find((icon) => icon.id === roleIcons.icon)?.icon ||
              SVG.assignedIcon,
          };
        })
        .sort((x, y) => {
          let a = x.title.toUpperCase(),
            b = y.title.toUpperCase();
          return a == b ? 0 : a > b ? 1 : -1;
        });
    }
    return [];
  }, [allUsers]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorTaskEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorTaskEl(null);
  };

  const handleAssigneeDropdownOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAssigneeDropdownEl(event.currentTarget);
  };

  const handleAssigneeDropdownClose = () => {
    setAssigneeDropdownEl(null);
  };

  const isTaskDone = task?.isCompleted;
  const canAddComments = task?.canAddComments;
  const addText = addTextEnabled && index === addTextIndex;

  const user = localStorage.getItem('user');
  const isMe = (commentedBy: string) =>
    user ? JSON.parse(user)?._id === commentedBy : false;

  const handleOpenChatInput = (type: 'close' | 'open', chat: any) => {
    if (type == 'open') {
      setAddTextIndex(index);
      setAddTextEnabled(true);
    } else {
      setAddedText('');
      setAddTextIndex(null);
      setAddTextEnabled(false);
    }
  };

  const handleToggleTaskStatus = () => {
    updateAnnouncementTaskMutation.mutate({
      id: task?.id,
      payload: { isCompleted: !task?.isCompleted },
    });
  };

  const handleMenuClick = (value: string) => {
    // Prevent actions if deletion is in progress
    if (deleteAnnouncementTaskMutation.isPending) return;
    switch (value) {
      case 'edit':
        setSelectedAnnouncementForEdit(announcement);
        setAnnouncementModalMode('add-task');
        setOpenAddEditAnnouncementDialog(true);
        break;
      case 'delete':
        setShowConfirmationDialog(true);
        break;
      default:
        break;
    }
  };

  const handleTickClick = (text: string, chat?: ChatType) => {
    if (text.trim() === '') return;
    handleOpenChatInput('close', null);

    if (chat) {
      if (text.trim() === chat.comment) return;
      updateAnnouncementTaskCommentMutation.mutate({
        id: chat?._id,
        payload: { comment: text },
      });
      return;
    }

    createAnnouncementTaskCommentMutation.mutate({
      id: task?.id,
      payload: { comment: text },
    });
  };

  const handleDeleteClick = (chat?: ChatType) => {
    if (chat) {
      deleteAnnouncementTaskCommentMutation.mutate(chat?._id);
    }
    handleOpenChatInput('close', null);
  };

  const handleAssigneeSelect = (value: string) => {
    updateAnnouncementTaskMutation.mutate({
      id: task?.id,
      payload: { recipients: [value] },
    });
    handleAssigneeDropdownClose();
  };

  const handleCloseConfirmationDialog = () => {
    setShowConfirmationDialog(false);
  };

  const handleConfirmDelete = () => {
    setShowConfirmationDialog(false);
    deleteAnnouncementTaskMutation.mutate(task?.id);
  };

  const taskAssigneeIconColor = useMemo(() => {
    const result = getIconColor(task?.recipients?.[0]?.roles?.[0]);
    return result;
  }, [task?.recipient]);

  const storedUser = localStorage.getItem('user');
  const currentUserId = useMemo(() => {
    return storedUser ? JSON.parse(storedUser)?._id : null;
  }, [storedUser]);

  const createdByMe = task?.createdBy?._id === currentUserId;
  const hasSameRole =
    task?.createdBy?.roles?.[0]?._id ===
    JSON.parse(storedUser!)?.roles?.[0]?._id;
  const showAnnouncementDoneCTA = useMemo(() => {
    if (!announcement) return false;
    const superAdmin = isSuperAdmin();
    const isAssignedToMe = task?.recipients?.some(
      (a: { _id: string }) => a._id === currentUserId,
    );
    if (createdByMe || isAssignedToMe || superAdmin) return true;
    if (announcement?.enabledDeletion && hasSameRole) return true;
    return false;
  }, [announcement, currentUserId]);

  return (
    <>
      <div
        key={task?.id}
        className={`w-full p-3 rounded-2xl flex flex-col gap-3 ${
          isTaskDone ? 'bg-[#DAFFF4]' : 'bg-[#8080801A]'
        } `}
      >
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-[15px] h-[21px]">
              <img
                src={SVG.taskIcon}
                alt="icon"
                className="w-full h-full object-contain"
              />
            </div>

            <span className={`text-black ${isTaskDone && 'line-through'}`}>
              {task?.description}
            </span>
          </div>

          <div className="flex items-center mr-auto gap-3 ">
            {isTaskDone && (
              <span className="text-grayText">
                {moment(task?.updatedAt).format('HH:mm DD/MM/YYYY')}
              </span>
            )}

            <AppMenu
              anchorEl={assigneeDropdownEl}
              open={Boolean(assigneeDropdownEl)}
              onOpen={handleAssigneeDropdownOpen}
              onClose={handleAssigneeDropdownClose}
              items={ASSIGNEE_ITEMS}
              onItemClick={handleAssigneeSelect}
              searchable={ASSIGNEE_ITEMS?.length >= 8}
              menuIcon={
                <div
                  className="px-2 py-2 rounded-[8px] cursor-pointer transition-colors duration-200"
                  style={{ backgroundColor: taskAssigneeIconColor.color }}
                  onClick={handleAssigneeDropdownOpen}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-[18px] h-[18px]">
                      <img
                        src={
                          ICONS.find(
                            (icon) => icon.id === taskAssigneeIconColor.icon,
                          )?.icon || SVG.assignedIcon
                        }
                        alt="icon"
                        className="w-full h-full object-contain"
                      />
                    </div>

                    <span className="text-sm text-darkerGray">
                      {task?.recipient?.assignedTo}
                    </span>

                    <div className="w-[7.5px] h-[3.75px]">
                      <img
                        src={SVG.dropdownIcon}
                        alt="icon"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>
              }
            />

            {showAnnouncementDoneCTA && (
              <button
                className="w-[24px] h-[24px]"
                onClick={handleToggleTaskStatus}
                disabled={addText}
              >
                <img
                  src={
                    isTaskDone
                      ? SVG.completedCheckboxIcon
                      : SVG.regularCheckboxIcon
                  }
                  alt="icon"
                  className="w-full h-full object-contain"
                />
              </button>
            )}

            <AppMenu
              anchorEl={anchorTaskEl}
              open={Boolean(anchorTaskEl)}
              onOpen={handleMenuOpen}
              onClose={handleMenuClose}
              items={MENUITEMS}
              onItemClick={handleMenuClick}
            />
          </div>
        </div>

        {task?.checklists?.length > 0 && (
          <div className="pr-5 flex flex-wrap items-center gap-2">
            {task?.checklists?.map((item: any, index: number) => (
              <ChecklistBox key={index} list={item} />
            ))}
          </div>
        )}

        {task?.files?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {task?.files?.map((file: any, index: number) => (
              <FileBox key={index} file={file} />
            ))}
          </div>
        )}

        {task?.comments?.map((comment: any) => {
          const me = isMe(comment?.commentBy?._id);
          return (
            <EditableChat
              key={comment._id}
              chat={comment}
              boxBg={me ? '#fff' : '#E9FAFF'}
              circleBg={
                me ? '#1AC3F2' : getCircleColor(comment?.commentBy?._id)
              }
              onSave={handleTickClick}
              onDelete={handleDeleteClick}
            />
          );
        })}

        {canAddComments && (
          <>
            {addText ? (
              <ChatInput
                value={addedText}
                onChange={(e) => setAddedText(e.target.value)}
                onTickClick={() => handleTickClick(addedText)}
                onDeleteClick={() => handleDeleteClick()}
              />
            ) : (
              !isTaskDone && (
                <div className="flex">
                  <AppButton
                    variant="secondary"
                    iconStart={
                      <img
                        src={SVG.addIcon}
                        className="w-[12px] h-[12px] object-contain"
                        alt="add"
                      />
                    }
                    className="rounded-lg text-gray px-3 border-none"
                    onClick={() => handleOpenChatInput('open', null)}
                  >
                    הוספת טקסט
                  </AppButton>
                </div>
              )
            )}
          </>
        )}
      </div>

      <AppConfirmationDialog
        open={showConfirmationDialog}
        onClose={handleCloseConfirmationDialog}
        onConfirm={handleConfirmDelete}
        icon={<img src={SVG.deleteRedIcon} className="w-[48px] h-[48px]" />}
        title="שימו לב"
        subTitle="הצ'קליסט יעבור לארכיון, האם להמשיך?"
        confirmBtnText="כן"
        cancelBtnText="לא"
      />
    </>
  );
}

export default TaskCard;
